import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useAPI from '../../hooks/useAPI';
import endpointsAdmin from '../../Services/endpointAdmin';
import api from '../../Services/api';
import { buildFileUrl } from '../../Services/api';
import { PENYALURAN_FUNDING_RULES, resolveProgramFundingMeta } from '../../Services/penyaluranFundingMap';
import {
  buildAdminMitraUserScope,
  extractArrayPayload,
  getAdminLembagaScope,
  matchItemToAdminScope,
} from './adminScope';

function resolvePenyaluranId(item) {
  return item?.id_penyaluran || item?.id_peyaluran || item?.id || '';
}

function formatCurrency(value) {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  }).format(Number(value || 0) || 0);
}

function resolveFileUrl(path) {
  return buildFileUrl(path);
}

function resolveStoredFundingType(item) {
  return String(
    item?.sumber_dana ||
    item?.sourceType ||
    item?.source_type ||
    item?.kategori_dana ||
    item?.jenis_sumber_dana ||
    ''
  )
    .trim()
    .toLowerCase();
}

function resolveFundingLabel(type, fallback = '-') {
  const rule = PENYALURAN_FUNDING_RULES.find((item) => item.type === type);
  return rule?.label || fallback;
}

function ListPenyaluranAdmin({ admin }) {
  const navigate = useNavigate();
  const location = useLocation();

  const { data, loading, error } = useAPI(endpointsAdmin.penyaluran_program.getAll);
  const { data: programData } = useAPI(endpointsAdmin.program.getAll);
  const { data: divisiData } = useAPI(endpointsAdmin.divisi.getAll);

  const [searchTerm, setSearchTerm] = useState('');
  const [actionMessage, setActionMessage] = useState(location.state?.message || '');
  const [adminMitraRecords, setAdminMitraRecords] = useState([]);

  const lembagaScope = getAdminLembagaScope(admin);

  useEffect(() => {
    let cancelled = false;

    const fetchAdminMitra = async () => {
      if (!lembagaScope.active) {
        setAdminMitraRecords([]);
        return;
      }

      try {
        const response = await api.post(endpointsAdmin.adminmitra.createByLembaga(lembagaScope.id), {});
        if (cancelled) return;
        setAdminMitraRecords(extractArrayPayload(response.data, ['adminmitra', 'data_adminmitra', 'users']));
      } catch (fetchError) {
        if (cancelled) return;
        setAdminMitraRecords([]);
      }
    };

    fetchAdminMitra();

    return () => {
      cancelled = true;
    };
  }, [lembagaScope.active, lembagaScope.id]);

  const adminMitraScope = useMemo(
    () => buildAdminMitraUserScope(adminMitraRecords),
    [adminMitraRecords]
  );

  useEffect(() => {
    if (location.state?.message) {
      setActionMessage(location.state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  const penyaluranList = useMemo(() => {
    const rawList = extractArrayPayload(data, ['penyaluran_program']);
    
    // Filter berdasarkan lembaga scope
    return rawList.filter((item) => matchItemToAdminScope(item, lembagaScope, adminMitraScope));
  }, [data, lembagaScope, adminMitraScope]);
  const programList = useMemo(() => extractArrayPayload(programData, ['program']), [programData]);
  const divisiList = useMemo(() => {
    const directDivisi = extractArrayPayload(divisiData, ['divisi']);
    if (directDivisi.length > 0) return directDivisi;

    const uniqueDivisi = new Map();
    programList.forEach((item) => {
      const idDivisi = String(item?.id_divisi || item?.divisi?.id_divisi || item?.divisi?.id || '').trim();
      const namaDivisi = String(item?.divisi?.nama_divisi || item?.nama_divisi || '').trim();
      if (idDivisi && namaDivisi && !uniqueDivisi.has(idDivisi)) {
        uniqueDivisi.set(idDivisi, { id_divisi: idDivisi, nama_divisi: namaDivisi });
      }
    });

    return Array.from(uniqueDivisi.values());
  }, [divisiData, programList]);

  const divisiMap = useMemo(
    () => new Map(divisiList.map((item) => [String(item.id_divisi), item.nama_divisi || 'Divisi'])),
    [divisiList]
  );

  const programMetadataMap = useMemo(() => {
    const map = new Map();
    programList.forEach((program) => {
      const fundingMeta = resolveProgramFundingMeta(program);
      map.set(String(program.id_program), {
        ...program,
        divisiName: program?.divisi?.nama_divisi || divisiMap.get(String(program.id_divisi)) || 'Divisi',
        sourceLabel: fundingMeta.sourceLabel,
        categoryName: fundingMeta.distributionLabel,
      });
    });
    return map;
  }, [divisiMap, programList]);

  const filteredList = useMemo(() => {
    const keyword = searchTerm.trim().toLowerCase();
    if (!keyword) return penyaluranList;

    return penyaluranList.filter((item) => {
      const rowId = String(resolvePenyaluranId(item)).trim();
      const programMeta = programMetadataMap.get(String(item.id_program));
      const storedFundingType = resolveStoredFundingType(item);
      const sourceLabel = resolveFundingLabel(storedFundingType, programMeta?.sourceLabel);

      return [
        rowId,
        programMeta?.divisiName,
        item.program?.nama_program || programMeta?.nama_program,
        sourceLabel,
        item.status_penyaluran,
        item.bukti_penyaluran,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(keyword));
    });
  }, [penyaluranList, programMetadataMap, searchTerm]);

  const currentError = error;

  const summaryStats = {
    totalItems: filteredList.length,
    totalNominal: filteredList.reduce((sum, item) => sum + (Number(item.nominal_penyaluran) || 0), 0),
    completed: filteredList.filter((item) => String(item.status_penyaluran || '').toLowerCase() === 'selesai').length,
  };

  if (loading) {
    return <div className="w-full px-6 lg:px-16 py-12 text-slate-600">Memuat data penyaluran...</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50/80 px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 text-white shadow-[0_20px_60px_rgba(15,23,42,0.16)]">
          <div className="flex flex-col gap-6 px-6 py-8 lg:flex-row lg:items-end lg:justify-between lg:px-8">
            <div>
              <div className="mb-3 inline-flex rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-blue-100 backdrop-blur">
                Penyaluran Program
              </div>
              <h1 className="text-3xl font-bold leading-tight lg:text-4xl">List Penyaluran</h1>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-200">
                Pantau hasil penyaluran secara rapi dengan tampilan yang lebih terstruktur dan mudah dibaca.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/penyaluran/input')}
              className="inline-flex items-center justify-center rounded-2xl bg-white px-4 py-2 text-sm font-semibold text-slate-800 transition hover:bg-slate-100"
            >
              Tambah Penyaluran
            </button>
          </div>
        </section>

        {(actionMessage || currentError) && (
          <div className={`rounded-2xl border px-4 py-3 text-sm font-semibold ${
            currentError ? 'border-red-200 bg-red-50 text-red-700' : 'border-blue-200 bg-blue-50 text-blue-700'
          }`}>
            {currentError || actionMessage}
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-3">
          <div className="rounded-[24px] border border-blue-200 bg-blue-50 p-4 shadow-sm">
            <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-blue-700">Total Data</p>
            <p className="mt-2 text-2xl font-bold text-slate-900">{summaryStats.totalItems}</p>
          </div>
          <div className="rounded-[24px] border border-emerald-200 bg-emerald-50 p-4 shadow-sm">
            <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-emerald-700">Total Nominal</p>
            <p className="mt-2 text-2xl font-bold text-slate-900">{formatCurrency(summaryStats.totalNominal)}</p>
          </div>
          <div className="rounded-[24px] border border-orange-200 bg-orange-50 p-4 shadow-sm">
            <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-orange-700">Selesai</p>
            <p className="mt-2 text-2xl font-bold text-slate-900">{summaryStats.completed}</p>
          </div>
        </div>

        <section className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-col gap-4 border-b border-slate-100 px-6 py-5 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Daftar Penyaluran</h2>
              <p className="mt-1 text-sm text-slate-500">Kelola hasil penyaluran dengan tampilan yang lebih konsisten dan mudah dibaca.</p>
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Cari penyaluran..."
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-orange-400 focus:bg-white focus:ring-2 focus:ring-orange-100 md:w-72"
            />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left font-semibold text-slate-700">Divisi</th>
                  <th className="px-6 py-4 text-left font-semibold text-slate-700">Program</th>
                  <th className="px-6 py-4 text-left font-semibold text-slate-700">Sumber Dana</th>
                  <th className="px-6 py-4 text-left font-semibold text-slate-700">Nominal</th>
                  <th className="px-6 py-4 text-left font-semibold text-slate-700">Status</th>
                  <th className="px-6 py-4 text-left font-semibold text-slate-700">Bukti</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredList.length === 0 ? (
                  <tr><td colSpan="6" className="px-6 py-12 text-center text-slate-500">Belum ada data penyaluran.</td></tr>
                ) : filteredList.map((item) => {
                  const rowId = resolvePenyaluranId(item);
                  const programMeta = programMetadataMap.get(String(item.id_program));
                  const proofUrl = resolveFileUrl(item.bukti_penyaluran);
                  const storedFundingType = resolveStoredFundingType(item);
                  const sourceLabel = resolveFundingLabel(storedFundingType, programMeta?.sourceLabel || '-');

                  return (
                    <tr key={rowId} className="hover:bg-slate-50/80">
                      <td className="px-6 py-4 text-slate-700">{programMeta?.divisiName || item.program?.divisi?.nama_divisi || '-'}</td>
                      <td className="px-6 py-4 text-slate-700">{item.program?.nama_program || programMeta?.nama_program || item.id_program || '-'}</td>
                      <td className="px-6 py-4 text-slate-700">
                        <div>
                          <p>{sourceLabel}</p>
                          <p className="text-xs text-slate-500">{programMeta?.categoryName || '-'}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-slate-700">{formatCurrency(item.nominal_penyaluran)}</td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                          {item.status_penyaluran || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-slate-700">
                        {proofUrl ? (
                          <a href={proofUrl} target="_blank" rel="noreferrer" className="text-blue-700 underline hover:text-blue-900">
                            Lihat Bukti
                          </a>
                        ) : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}

export default ListPenyaluranAdmin;
